import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { get, omit, pick } from "lodash";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";

import MeterDetails, {
  inputFields as meterDetailsFields,
} from "Components/MeterInfoForm/MeterDetails";
import SealDetails, {
  inputFields as sealDetailsFields,
} from "Components/MeterInfoForm/SealDetails";
import ServiceConnectionDetails, {
  inputFields as serviceConnectionDetailsFields,
} from "Components/MeterInfoForm/ServiceConnectionDetails";
import AccucheckTest, {
  inputFields as accucheckTestFields,
} from "Components/MeterInfoForm/AccucheckTest";
import SolarMeterReading, {
  inputFields as solarMeterReadingFields,
} from "Components/MeterInfoForm/SolarMeterReading";

import { inputFields as meterReadingFields } from "Components/MeterInfoForm/MeterReading";
import Stepper from "common/Stepper";
import { meterKeys, meterReadingKeys, otherMeterId } from "common/constant";
import MeterReading from "Components/MeterInfoForm/MeterReading";
import { getOptionValue } from "common/enums";
import Backdrop from "Components/Common/Backdrop";
import Modal from "Components/Common/Modal";
import ConsumerDetails from "Components/MeterInfoForm/ConsumerDetails";

import generateValidationSchema from "utils/generateValidationSchema";
import { filterEmptyStrings } from "utils";
import filterEmptyObjects from "utils/filterEmptyObjects";

import {
  useGetConsumerMeterDetailsByConsumerId,
  useLazyGetMeterDetailsByMeterId,
  useLazyGetMeterDetailsByTypeId,
  usePostMeterInfoMutation,
} from "apis/UtilityConsumerMeter";

import { UTILITY_CONSUMER } from "tagTypes";

const keysToDelete = ["extraValues", "consumerInfo"];

const EditUtilityConsumerMeterInfo = ({
  meterId,
  consumerId,
  handleClose,
  isSolarMeter = false,
}) => {
  const [currentStepName, setCurrentStepName] = useState("Consumer Details");
  const [currentStep, setCurrentStep] = useState(0);
  const [meterData, setMeterData] = useState();
  const [meterType, setMeterType] = useState(
    getOptionValue("meterTypes", "Liberty 200")
  );

  const { data: { data: utilityConsumerData } = {}, isLoading } =
    useGetConsumerMeterDetailsByConsumerId(consumerId);
  const [getMeterByMeterId, { isLoading: isUtilityConsumerMeterLoading }] =
    useLazyGetMeterDetailsByMeterId();

  const [updateMeterInfo, { isLoading: isMeterDataUpdating }] =
    usePostMeterInfoMutation();

  const [getMeterDetails, { isLoading: isMeterDetailsLoading }] =
    useLazyGetMeterDetailsByTypeId();

  const validationSchemas = useMemo(
    () => ({
      "Consumer Details": Yup.object(),
      "Meter Details": Yup.object({
        meter: Yup.object(generateValidationSchema(meterDetailsFields())),
      }),
      "Seal Details": Yup.object({
        seal: Yup.object(generateValidationSchema(sealDetailsFields)),
      }),
      "Service Connection Details": Yup.object({
        serviceConnection: Yup.object(
          generateValidationSchema(serviceConnectionDetailsFields)
        ),
      }),
      "Accucheck Test": Yup.object({
        accucheckTest: Yup.object(
          generateValidationSchema(accucheckTestFields)
        ),
      }),
      "Initial Meter Reading": Yup.object({
        initialMeterReading: Yup.object(
          generateValidationSchema(meterReadingFields[meterType])
        ),
      }),
      "Final Meter Reading": Yup.object({
        finalMeterReading: Yup.object(
          generateValidationSchema(meterReadingFields[meterType])
        ),
      }),
      "Initial Solar Reading": Yup.object({
        initialSolarMeterReadingDetail: Yup.object(
          generateValidationSchema(solarMeterReadingFields)
        ),
      }),

      "Final Solar Reading": Yup.object({
        finalSolarMeterReadingDetail: Yup.object(
          generateValidationSchema(solarMeterReadingFields)
        ),
      }),
    }),
    [meterType]
  );

  const initialValues = useMemo(
    () => ({
      consumerInfo: {
        id: utilityConsumerData?.id ?? "",
        name: utilityConsumerData?.consumerName ?? "",
        address: utilityConsumerData?.utilityAddress ?? "",
        serviceNo: utilityConsumerData?.consumerNumber ?? "",
        typeOfConnection: utilityConsumerData?.typeOfConnection ?? "",
        contractLoad: utilityConsumerData?.contractLoad ?? "",
        typeOfTariff: utilityConsumerData?.tariffName ?? "",
        meter: {
          id: meterId ?? "",
          srNo: utilityConsumerData?.meter?.srNo ?? "",
        },
      },
      meter: {
        id: meterData?.meter?.id ?? 0,
        make: meterData?.meter?.make ?? "",
        type: meterData?.meter?.type ?? "",
        srNo: meterData?.meter?.srNo ?? "",
        labNo: meterData?.meter?.labNo ?? "",
        phaseOrWire: meterData?.meter?.phaseOrWire ?? "",
        voltageRating: meterData?.meter?.voltageRating ?? "",
        currentRating: meterData?.meter?.currentRating ?? "",
        meterCTR: meterData?.meter?.meterCTR ?? "",
        meterPTR: meterData?.meter?.meterPTR ?? "",
        pulseOrkwH: meterData?.meter?.pulseOrkwH ?? "",
        class: meterData?.meter?.class ?? "",
        dialMF: meterData?.meter?.dialMF ?? "",
      },
      seal: {
        id: meterData?.seal?.id ?? 0,
        meterBodySealLeftBottom: meterData?.seal?.meterBodySealLeftBottom ?? "",
        meterBodySealLeftTop: meterData?.seal?.meterBodySealLeftTop ?? "",
        meterBodySealRightTop: meterData?.seal?.meterBodySealRightTop ?? "",
        meterBodySealRightBottom:
          meterData?.seal?.meterBodySealRightBottom ?? "",
        meterTerminalCoverSealLeft:
          meterData?.seal?.meterTerminalCoverSealLeft ?? "",
        meterTerminalCoverSealRight:
          meterData?.seal?.meterTerminalCoverSealRight ?? "",
        meterResetButtonSeal: meterData?.seal?.meterResetButtonSeal ?? "",
        meterBoxOrPanelSealTop: meterData?.seal?.meterBoxOrPanelSealTop ?? "",
        meterBoxOrPanelSealMiddle:
          meterData?.seal?.meterBoxOrPanelSealMiddle ?? "",
        meterBoxOrPanelSealBottom:
          meterData?.seal?.meterBoxOrPanelSealBottom ?? "",
        testTerminalBlockSealLeft:
          meterData?.seal?.testTerminalBlockSealLeft ?? "",
        testTerminalBlockSealRight:
          meterData?.seal?.testTerminalBlockSealRight ?? "",
        lineCTSealRPhase: meterData?.seal?.lineCTSealRPhase ?? "",
        lineCTSealYPhase: meterData?.seal?.lineCTSealYPhase ?? "",
        lineCTSealBPhase: meterData?.seal?.lineCTSealBPhase ?? "",
        ptChangeOverBoxSealTop: meterData?.seal?.ptChangeOverBoxSealTop ?? "",
        ptChangeOverBoxSealBottom:
          meterData?.seal?.ptChangeOverBoxSealBottom ?? "",
      },
      serviceConnection: {
        id: meterData?.serviceConnection?.id ?? 0,
        lineCTRatio: meterData?.serviceConnection?.lineCTRatio ?? "",
        linePTRatio: meterData?.serviceConnection?.linePTRatio ?? "",
        rPhase: "",
        yPhase: "",
        bPhase: "",
        finalMF: meterData?.serviceConnection?.finalMF ?? "",
      },
      accucheckTest: {
        meterDisplayDetail: {
          rn: meterData?.accucheckTest?.meterDisplayDetail?.rn ?? "",
          yn: meterData?.accucheckTest?.meterDisplayDetail?.yn ?? "",
          bn: meterData?.accucheckTest?.meterDisplayDetail?.bn ?? "",
          id: meterData?.accucheckTest?.meterDisplayDetail?.id ?? 0,
          kw: meterData?.accucheckTest?.meterDisplayDetail?.kw ?? "",
          ir: meterData?.accucheckTest?.meterDisplayDetail?.ir ?? "",
          iy: meterData?.accucheckTest?.meterDisplayDetail?.iy ?? "",
          ib: meterData?.accucheckTest?.meterDisplayDetail?.ib ?? "",
          pf: meterData?.accucheckTest?.meterDisplayDetail?.pf ?? "",
        },
        accucheckDetail: {
          id: meterData?.accucheckTest?.accucheckDetail?.id ?? 0,
          rn: meterData?.accucheckTest?.accucheckDetail?.rn ?? "",
          yn: meterData?.accucheckTest?.accucheckDetail?.yn ?? "",
          bn: meterData?.accucheckTest?.accucheckDetail?.bn ?? "",
          pf: meterData?.accucheckTest?.accucheckDetail?.pf ?? "",
          ir: meterData?.accucheckTest?.accucheckDetail?.ir ?? "",
          iy: meterData?.accucheckTest?.accucheckDetail?.iy ?? "",
          ib: meterData?.accucheckTest?.accucheckDetail?.ib ?? "",
          hz: meterData?.accucheckTest?.accucheckDetail?.hz ?? "",
          phaseSequence:
            meterData?.accucheckTest?.accucheckDetail?.phaseSequence ?? "",
          p_KW: meterData?.accucheckTest?.accucheckDetail?.p_KW ?? "",
          q_KVAR: meterData?.accucheckTest?.accucheckDetail?.q_KVAR ?? "",
          s_KVA: meterData?.accucheckTest?.accucheckDetail?.s_KVA ?? "",
          f_KWH: meterData?.accucheckTest?.accucheckDetail?.f_KWH ?? "",
        },
        meterReadingTest: {
          id: meterData?.accucheckTest?.meterReadingTest?.id ?? 0,
          final: meterData?.accucheckTest?.meterReadingTest?.final ?? "",
          initial: meterData?.accucheckTest?.meterReadingTest?.initial ?? "",
          difference:
            meterData?.accucheckTest?.meterReadingTest?.difference ?? "",
          error: meterData?.accucheckTest?.meterReadingTest?.error ?? "",
        },
      },
      finalMeterReading: {
        id: meterData?.finalMeterReading?.id ?? 0,
        instantaneousVoltageV:
          meterData?.finalMeterReading?.instantaneousVoltageV ?? "",
        instantaneousCurrentA:
          meterData?.finalMeterReading?.instantaneousCurrentA ?? "",
        instantaneousActivePowerkW:
          meterData?.finalMeterReading?.instantaneousActivePowerkW ?? "",
        activeImportEnergy:
          meterData?.finalMeterReading?.activeImportEnergy ?? "",
        activeExportEnergy:
          meterData?.finalMeterReading?.activeExportEnergy ?? "",
        currentImportMD: meterData?.finalMeterReading?.currentImportMD ?? "",
        currentExportMD: meterData?.finalMeterReading?.currentExportMD ?? "",
        rPhaseVoltage: meterData?.finalMeterReading?.rPhaseVoltage ?? "",
        yPhaseVoltage: meterData?.finalMeterReading?.yPhaseVoltage ?? "",
        bPhaseVoltage: meterData?.finalMeterReading?.bPhaseVoltage ?? "",
        rPhaseCurrent: meterData?.finalMeterReading?.rPhaseCurrent ?? "",
        yPhaseCurrent: meterData?.finalMeterReading?.yPhaseCurrent ?? "",
        bPhaseCurrent: meterData?.finalMeterReading?.bPhaseCurrent ?? "",
        neutralCurrent: meterData?.finalMeterReading?.neutralCurrent ?? "",
        instActivePower: meterData?.finalMeterReading?.instActivePower ?? "",
        instApparentPower:
          meterData?.finalMeterReading?.instApparentPower ?? "",
        instantaneousPF: meterData?.finalMeterReading?.instantaneousPF ?? "",
        frequency: meterData?.finalMeterReading?.frequency ?? "",
        apparentImportEnergy:
          meterData?.finalMeterReading?.apparentImportEnergy ?? "",
        apparentExportEnergy:
          meterData?.finalMeterReading?.apparentExportEnergy ?? "",
        reactiveEnergyQ1: meterData?.finalMeterReading?.reactiveEnergyQ1 ?? "",
        reactiveEnergyQ2: meterData?.finalMeterReading?.reactiveEnergyQ2 ?? "",
        reactiveEnergyQ3: meterData?.finalMeterReading?.reactiveEnergyQ3 ?? "",
        reactiveEnergyQ4: meterData?.finalMeterReading?.reactiveEnergyQ4 ?? "",
        currentApparentImportMD:
          meterData?.finalMeterReading?.currentApparentImportMD ?? "",
        currentApparentExportMD:
          meterData?.finalMeterReading?.currentApparentExportMD ?? "",
        averagePowerFactor:
          meterData?.finalMeterReading?.averagePowerFactor ?? "",
        phaseSequence: meterData?.finalMeterReading?.phaseSequence ?? "",
        currentActiveImportPeakHourEnergyMWhTOD1:
          meterData?.finalMeterReading
            ?.currentActiveImportPeakHourEnergyMWhTOD1 ?? "",
        currentActiveImportPeakHourEnergyMWhTOD5:
          meterData?.finalMeterReading
            ?.currentActiveImportPeakHourEnergyMWhTOD5 ?? "",
        currentActiveExportPeakHourEnergyMWh:
          meterData?.finalMeterReading?.currentActiveExportPeakHourEnergyMWh ??
          "",
        currentActiveImportNightHourEnergyMWh:
          meterData?.finalMeterReading?.currentActiveImportNightHourEnergyMWh ??
          "",
        currentActiveExportNightHourEnergyMWh:
          meterData?.finalMeterReading?.currentActiveExportNightHourEnergyMWh ??
          "",
        currentActiveImportRestHourEnergyMWh:
          meterData?.finalMeterReading?.currentActiveImportRestHourEnergyMWh ??
          "",
        currentActiveExportRestHourEnergyMWh:
          meterData?.finalMeterReading?.currentActiveExportRestHourEnergyMWh ??
          "",
        hist1ActiveImportEnergy:
          meterData?.finalMeterReading?.hist1ActiveImportEnergy ?? "",
        hist1ActiveExportEnergy:
          meterData?.finalMeterReading?.hist1ActiveExportEnergy ?? "",
        hist2ActiveImportEnergy:
          meterData?.finalMeterReading?.hist2ActiveImportEnergy ?? "",
        hist2ActiveExportEnergy:
          meterData?.finalMeterReading?.hist2ActiveExportEnergy ?? "",
        hist1ActiveImportMD:
          meterData?.finalMeterReading?.hist1ActiveImportMD ?? "",
        hist1ActiveExportMD:
          meterData?.finalMeterReading?.hist1ActiveExportMD ?? "",
        hist2ActiveImportMD:
          meterData?.finalMeterReading?.hist2ActiveImportMD ?? "",
        hist2ActiveExportMD:
          meterData?.finalMeterReading?.hist2ActiveExportMD ?? "",
        hist1ApparentImportMD:
          meterData?.finalMeterReading?.hist1ApparentImportMD ?? "",
        hist1ApparentExportMD:
          meterData?.finalMeterReading?.hist1ApparentExportMD ?? "",
        hist2ApparentImportMD:
          meterData?.finalMeterReading?.hist2ApparentImportMD ?? "",
        hist2ApparentExportMD:
          meterData?.finalMeterReading?.hist2ApparentExportMD ?? "",
        currentDefrauded: meterData?.finalMeterReading?.currentDefrauded ?? "",
        cumulativeTamperCount:
          meterData?.finalMeterReading?.cumulativeTamperCount ?? "",
        activeExportSetOffEnergyTOD1:
          meterData?.finalMeterReading?.activeExportSetOffEnergyTOD1 ?? "",
        activeImportSetOffEnergyTOD1:
          meterData?.finalMeterReading?.activeImportSetOffEnergyTOD1 ?? "",
        activeExportSetOffEnergyTOD3:
          meterData?.finalMeterReading?.activeExportSetOffEnergyTOD3 ?? "",
        activeImportSetOffEnergyTOD3:
          meterData?.finalMeterReading?.activeImportSetOffEnergyTOD3 ?? "",
      },
      initialMeterReading: {
        id: meterData?.initialMeterReading?.id ?? 0,
        instantaneousVoltageV:
          meterData?.initialMeterReading?.instantaneousVoltageV ?? "",
        instantaneousCurrentA:
          meterData?.initialMeterReading?.instantaneousCurrentA ?? "",
        instantaneousActivePowerkW:
          meterData?.initialMeterReading?.instantaneousActivePowerkW ?? "",
        activeImportEnergy:
          meterData?.initialMeterReading?.activeImportEnergy ?? "",
        activeExportEnergy:
          meterData?.initialMeterReading?.activeExportEnergy ?? "",
        currentImportMD: meterData?.initialMeterReading?.currentImportMD ?? "",
        currentExportMD: meterData?.initialMeterReading?.currentExportMD ?? "",
        rPhaseVoltage: meterData?.initialMeterReading?.rPhaseVoltage ?? "",
        yPhaseVoltage: meterData?.initialMeterReading?.yPhaseVoltage ?? "",
        bPhaseVoltage: meterData?.initialMeterReading?.bPhaseVoltage ?? "",
        rPhaseCurrent: meterData?.initialMeterReading?.rPhaseCurrent ?? "",
        yPhaseCurrent: meterData?.initialMeterReading?.yPhaseCurrent ?? "",
        bPhaseCurrent: meterData?.initialMeterReading?.bPhaseCurrent ?? "",
        neutralCurrent: meterData?.initialMeterReading?.neutralCurrent ?? "",
        instActivePower: meterData?.initialMeterReading?.instActivePower ?? "",
        instApparentPower:
          meterData?.initialMeterReading?.instApparentPower ?? "",
        instantaneousPF: meterData?.initialMeterReading?.instantaneousPF ?? "",
        frequency: meterData?.initialMeterReading?.frequency ?? "",
        apparentImportEnergy:
          meterData?.initialMeterReading?.apparentImportEnergy ?? "",
        apparentExportEnergy:
          meterData?.initialMeterReading?.apparentExportEnergy ?? "",
        reactiveEnergyQ1:
          meterData?.initialMeterReading?.reactiveEnergyQ1 ?? "",
        reactiveEnergyQ2:
          meterData?.initialMeterReading?.reactiveEnergyQ2 ?? "",
        reactiveEnergyQ3:
          meterData?.initialMeterReading?.reactiveEnergyQ3 ?? "",
        reactiveEnergyQ4:
          meterData?.initialMeterReading?.reactiveEnergyQ4 ?? "",
        currentApparentImportMD:
          meterData?.initialMeterReading?.currentApparentImportMD ?? "",
        currentApparentExportMD:
          meterData?.initialMeterReading?.currentApparentExportMD ?? "",
        averagePowerFactor:
          meterData?.initialMeterReading?.averagePowerFactor ?? "",
        phaseSequence: meterData?.initialMeterReading?.phaseSequence ?? "",
        currentActiveImportPeakHourEnergyMWhTOD1:
          meterData?.initialMeterReading
            ?.currentActiveImportPeakHourEnergyMWhTOD1 ?? "",
        currentActiveImportPeakHourEnergyMWhTOD5:
          meterData?.initialMeterReading
            ?.currentActiveImportPeakHourEnergyMWhTOD5 ?? "",
        currentActiveExportPeakHourEnergyMWh:
          meterData?.initialMeterReading
            ?.currentActiveExportPeakHourEnergyMWh ?? "",
        currentActiveImportNightHourEnergyMWh:
          meterData?.initialMeterReading
            ?.currentActiveImportNightHourEnergyMWh ?? "",
        currentActiveExportNightHourEnergyMWh:
          meterData?.initialMeterReading
            ?.currentActiveExportNightHourEnergyMWh ?? "",
        currentActiveImportRestHourEnergyMWh:
          meterData?.initialMeterReading
            ?.currentActiveImportRestHourEnergyMWh ?? "",
        currentActiveExportRestHourEnergyMWh:
          meterData?.initialMeterReading
            ?.currentActiveExportRestHourEnergyMWh ?? "",
        hist1ActiveImportEnergy:
          meterData?.initialMeterReading?.hist1ActiveImportEnergy ?? "",
        hist1ActiveExportEnergy:
          meterData?.initialMeterReading?.hist1ActiveExportEnergy ?? "",
        hist2ActiveImportEnergy:
          meterData?.initialMeterReading?.hist2ActiveImportEnergy ?? "",
        hist2ActiveExportEnergy:
          meterData?.initialMeterReading?.hist2ActiveExportEnergy ?? "",
        hist1ActiveImportMD:
          meterData?.initialMeterReading?.hist1ActiveImportMD ?? "",
        hist1ActiveExportMD:
          meterData?.initialMeterReading?.hist1ActiveExportMD ?? "",
        hist2ActiveImportMD:
          meterData?.initialMeterReading?.hist2ActiveImportMD ?? "",
        hist2ActiveExportMD:
          meterData?.initialMeterReading?.hist2ActiveExportMD ?? "",
        hist1ApparentImportMD:
          meterData?.initialMeterReading?.hist1ApparentImportMD ?? "",
        hist1ApparentExportMD:
          meterData?.initialMeterReading?.hist1ApparentExportMD ?? "",
        hist2ApparentImportMD:
          meterData?.initialMeterReading?.hist2ApparentImportMD ?? "",
        hist2ApparentExportMD:
          meterData?.initialMeterReading?.hist2ApparentExportMD ?? "",
        currentDefrauded:
          meterData?.initialMeterReading?.currentDefrauded ?? "",
        cumulativeTamperCount:
          meterData?.initialMeterReading?.cumulativeTamperCount ?? "",
        activeExportSetOffEnergyTOD1:
          meterData?.initialMeterReading?.activeExportSetOffEnergyTOD1 ?? "",
        activeImportSetOffEnergyTOD1:
          meterData?.initialMeterReading?.activeImportSetOffEnergyTOD1 ?? "",
        activeExportSetOffEnergyTOD3:
          meterData?.initialMeterReading?.activeExportSetOffEnergyTOD3 ?? "",
        activeImportSetOffEnergyTOD3:
          meterData?.initialMeterReading?.activeImportSetOffEnergyTOD3 ?? "",
      },
      finalSolarMeterReadingDetail: {
        id: meterData?.solarFinalMeterReadingDetail?.id ?? 0,
        solarEnergy: meterData?.solarFinalMeterReadingDetail?.solarEnergy ?? 0,
      },
      initialSolarMeterReadingDetail: {
        id: meterData?.solarInitialMeterReadingDetail?.id ?? 0,
        solarEnergy:
          meterData?.solarInitialMeterReadingDetail?.solarEnergy ?? 0,
      },
      extraValues: {
        isMeterChanged: !!(meterId && meterData?.meter?.id
          ? meterData?.meter.id !== meterId
          : true),

        meterId: meterData?.meter?.id ?? otherMeterId,
        srNo: meterData?.meter?.srNo ?? "",
      },
    }),
    [meterData, utilityConsumerData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: validationSchemas[currentStepName],
    initialValues,
    onSubmit: async (values, { setTouched }) => {
      const isLastStep = currentStep === steps.length - 1;

      const payload = filterEmptyObjects(filterEmptyStrings(values));

      if (isLastStep) {
        await updateMeterInfo({
          payload: omit(
            payload,
            isSolarMeter
              ? [...keysToDelete, "initialMeterReading", "finalMeterReading"]
              : [
                  ...keysToDelete,
                  "finalSolarMeterReadingDetail",
                  "initialSolarMeterReadingDetail",
                ]
          ),
          query: {
            utilityConsumerId: values.consumerInfo?.id,
            isSolarMeter,
          },
          toastMessage: "Meter details updated successfully",
          tags: [UTILITY_CONSUMER],
        });

        handleClose();
      } else {
        setCurrentStep((prev) => prev + 1);
        setTouched({});
      }
    },
  });

  const {
    handleSubmit,
    setFieldValue,
    values: {
      extraValues: { isMeterChanged, meterId: selectedMeterId },
      meter: { type },
    },
  } = formik;

  const steps = useMemo(() => {
    const initialSteps = [
      {
        id: 1,
        name: "Consumer Details",
        isCompleted: currentStep >= 1,
        component: <ConsumerDetails formik={formik} isUtilityConsumerModal />,
      },
      {
        id: 2,
        name: "Meter Details",
        isCompleted: currentStep >= 2,
        component: <MeterDetails formik={formik} />,
      },
      {
        id: 3,
        name: "Seal Details",
        isCompleted: currentStep >= 3,
        component: <SealDetails formik={formik} />,
      },
      {
        id: 4,
        name: "Service Connection Details",
        isCompleted: currentStep >= 4,
        component: <ServiceConnectionDetails formik={formik} />,
      },
      {
        id: 5,
        name: "Accucheck Test",
        isCompleted: currentStep >= 5,
        component: <AccucheckTest formik={formik} />,
      },
    ];

    if (isMeterChanged && selectedMeterId !== meterId) {
      if (isSolarMeter) {
        initialSteps.splice(
          initialSteps.length,
          0,
          {
            id: 6,
            name: "Final Solar Reading",
            isCompleted: currentStep >= 6,
            component: (
              <SolarMeterReading
                parentKey="finalSolarMeterReadingDetail"
                formik={formik}
              />
            ),
          },
          {
            id: 7,
            name: "Initial Solar Reading",
            isCompleted: currentStep >= 7,
            component: (
              <SolarMeterReading
                parentKey="initialSolarMeterReadingDetail"
                formik={formik}
              />
            ),
          }
        );
      } else {
        initialSteps.splice(
          initialSteps.length,
          0,
          {
            id: 6,
            name: "Final Meter Reading",
            isCompleted: currentStep >= 6,
            component: (
              <MeterReading
                parentKey="finalMeterReading"
                formik={formik}
                initialMeterReading={meterData?.initialMeterReading || {}}
                finalMeterReading={meterData?.finalMeterReading || {}}
              />
            ),
          },
          {
            id: 7,
            name: "Initial Meter Reading",
            isCompleted: currentStep >= 7,
            component: (
              <MeterReading
                parentKey="initialMeterReading"
                formik={formik}
                initialMeterReading={meterData?.initialMeterReading || {}}
                finalMeterReading={meterData?.finalMeterReading || {}}
              />
            ),
          }
        );
      }
    }
    return initialSteps;
  }, [selectedMeterId, meterData, currentStep, formik, isSolarMeter]);

  const getMeterDetailsByMeterId = async (meterId) => {
    try {
      const meterDetails = await getMeterByMeterId(meterId);

      const meterInfo = pick(meterDetails.data, [
        "meter",
        "seal",
        "serviceConnection",
        "accucheckTest",
        "initialSolarMeterReadingDetail",
      ]);

      setMeterData(meterInfo);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const stepName = steps.find((_, index) => index === currentStep).name;
    if (stepName !== currentStepName) {
      setCurrentStepName(stepName);
    }
  }, [currentStep, steps]);

  useEffect(() => {
    if (type !== meterData?.meter?.type) {
      (async () => {
        try {
          const { data } = await getMeterDetails({
            meterVariantType: type,
          });
          meterKeys.forEach((key) =>
            setFieldValue(`meter.${key}`, get(data, key) ?? "")
          );
        } catch (error) {}
      })();
      meterReadingKeys.forEach(({ key }) => {
        setFieldValue(`finalMeterReading.${key}`, "");
        setFieldValue(`initialMeterReading.${key}`, "");
      });
    }

    setMeterType(type);
  }, [type]);
  // changeMeter
  useEffect(() => {
    if (!isMeterChanged && meterId) {
      getMeterDetailsByMeterId(meterId);
    }
  }, [isMeterChanged]);

  // change meterInfo if meter is changed
  useEffect(() => {
    if (selectedMeterId === otherMeterId) {
      setMeterData(null);
    } else if (meterData?.meter?.id !== selectedMeterId && selectedMeterId) {
      getMeterDetailsByMeterId(selectedMeterId);
    }
  }, [selectedMeterId]);

  // set initial meter info
  useEffect(() => {
    if (meterId) {
      getMeterDetailsByMeterId(meterId);
    }
  }, [meterId]);

  if (isUtilityConsumerMeterLoading || isMeterDetailsLoading || isLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title="Meter Information"
      maxWidth="lg"
    >
      <div className="stepper-container">
        <Stepper
          steps={steps}
          handleSubmit={handleSubmit}
          activeStepIndex={currentStep}
          setActiveStepIndex={setCurrentStep}
          isLoading={isMeterDataUpdating}
          formik={formik}
          isDraftDisabled={true}
        />
      </div>
    </Modal>
  );
};

EditUtilityConsumerMeterInfo.propTypes = {
  meterId: PropTypes.number,
  handleClose: PropTypes.func,
  consumerId: PropTypes.number,
  isSolarMeter: PropTypes.bool,
};

export default withTranslation()(EditUtilityConsumerMeterInfo);
